import { Link, useParams } from 'react-router';
import { EmptyState } from '@/components/ui/EmptyState';
import { useAuth } from '@/features/auth/use-auth';
import { formatDateRange, formatLira, ticketRule } from '../format';
import { TicketJoinPanel } from '../components/TicketJoinPanel';
import { useTicketEvent } from '../api';
import '../tickets.css';

const phaseCopy: Record<string, { text: string; className: string }> = {
  running: { text: 'Etkinlik açık', className: 'bg-emerald-500/15 text-emerald-300' },
  upcoming: { text: 'Yakında', className: 'bg-sky-500/15 text-sky-300' },
  ended: { text: 'Sona erdi', className: 'bg-surface-700/60 text-surface-300' },
  archived: { text: 'Arşivlendi', className: 'bg-surface-700/60 text-surface-300' },
};

/**
 * One event in full: what it is, what it pays in tickets, and the join flow.
 *
 * The rule sits above the form rather than inside it — the number that decides
 * whether somebody bothers is not a form detail.
 */
export function TicketEventDetailPage() {
  const { slug } = useParams();
  const { isLoading: authLoading } = useAuth();
  const event = useTicketEvent(slug);

  // The query stays disabled until the session probe settles, so it is pending
  // without fetching during that window. Show the skeleton rather than the
  // "not found" panel that an absent `data` would otherwise produce.
  if (authLoading || event.isLoading) {
    return (
      <p className="py-20 text-center text-surface-500" role="status">
        Yükleniyor…
      </p>
    );
  }

  if (event.isError || !event.data) {
    return (
      <EmptyState title="Böyle bir bilet etkinliği bulunamadı.">
        <Link to="/ticket-events" className="mt-4 inline-block text-sm text-brand-400 hover:underline">
          Tüm etkinlikler
        </Link>
      </EmptyState>
    );
  }

  const data = event.data;
  const phase = phaseCopy[data.phase] ?? phaseCopy.ended!;

  return (
    <div className="w-full py-2">
      <Link to="/ticket-events" className="text-sm text-surface-500 transition hover:text-surface-300">
        ← Tüm etkinlikler
      </Link>

      <article
        className="ticket-hero relative mt-5 overflow-hidden rounded-[var(--radius-card)] border border-surface-800 p-5 sm:p-7"
        style={{ boxShadow: `inset 0 1px 0 0 ${data.siteBrandColor}22` }}
      >
        <div className="flex flex-wrap items-center gap-3">
          <span
            className="grid size-14 shrink-0 place-items-center rounded-full p-[2px]"
            style={{ background: `linear-gradient(160deg, ${data.siteBrandColor}66, transparent 70%)` }}
          >
            {data.siteIconUrl ? (
              <img
                src={data.siteIconUrl}
                alt=""
                width={52}
                height={52}
                className="size-13 rounded-full object-cover"
              />
            ) : (
              <span className="grid size-13 place-items-center rounded-full bg-surface-800 text-lg font-bold text-surface-300">
                {data.siteName.slice(0, 1)}
              </span>
            )}
          </span>

          <div className="min-w-0 flex-1">
            <p className="text-xs uppercase tracking-widest text-surface-500">{data.siteName}</p>
            <h1 className="font-display text-2xl font-bold tracking-tight text-surface-50 sm:text-3xl">
              {data.title}
            </h1>
          </div>

          <span className={`rounded-full px-3 py-1 text-xs font-bold ${phase.className}`}>
            {phase.text}
          </span>
        </div>

        {data.description ? (
          <p className="mt-5 whitespace-pre-line text-sm leading-relaxed text-surface-400">
            {data.description}
          </p>
        ) : null}

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="ticket-rule-box rounded-xl border border-brand-500/25 bg-brand-500/[0.06] px-4 py-4">
            <p className="text-xs uppercase tracking-widest text-surface-500">Bilet kuralı</p>
            <p className="mt-1 font-display text-2xl font-bold text-brand-400">
              {ticketRule(data.depositThreshold, data.ticketsPerThreshold)}
            </p>
            <p className="mt-1 text-xs text-surface-500">
              Eşik: {formatLira(data.depositThreshold)} · Onaydan sonra çekilişe eklenir.
            </p>
          </div>

          <div className="rounded-xl border border-surface-800 bg-surface-950/40 px-4 py-4">
            <p className="text-xs uppercase tracking-widest text-surface-500">Katılım aralığı</p>
            <p className="mt-1 text-sm font-medium text-surface-200">
              {formatDateRange(data.startsAtUtc, data.endsAtUtc)}
            </p>
            <p className="mt-1 text-xs text-surface-500">
              Yatırım tarihi bu aralığın dışındaysa başvuru reddedilir.
            </p>
          </div>
        </div>
        {data.prizes.length > 0 ? (
          <section aria-labelledby="prizes" className="mt-6 border-t border-surface-800 pt-5">
            <h2 id="prizes" className="text-xs uppercase tracking-widest text-surface-500">
              Ödüller
            </h2>
            <ol className="mt-3 grid gap-2">
              {data.prizes.map((prize) => (
                <li
                  key={prize.fromRank}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-surface-800 bg-surface-950/40 px-4 py-3"
                >
                  <span className="font-mono text-sm text-brand-400">{prize.rankLabel}</span>
                  <span className="text-sm text-surface-200">{prize.label}</span>
                </li>
              ))}
            </ol>
          </section>
        ) : null}
      </article>

      <section aria-labelledby="join" className="ticket-join mt-6 rounded-[var(--radius-card)] border border-surface-800 bg-surface-900/60 p-5 sm:p-7">
        <h2 id="join" className="section-title">
          Katılım
        </h2>
        <div className="mt-4">
          <TicketJoinPanel event={data} />
        </div>
      </section>

      <section aria-labelledby="how" className="mt-8">
        <h2 id="how" className="section-title">
          Nasıl işliyor?
        </h2>
        <ol className="mt-3 grid gap-3 sm:grid-cols-3">
          <li className="ticket-step rounded-xl border border-surface-800 p-4 text-sm text-surface-400">
            <span className="mb-2 block font-mono text-xs text-brand-400">01</span>
            {data.siteName} hesabının kullanıcı adını veya ID&apos;sini profilinden kaydet.
          </li>
          <li className="ticket-step rounded-xl border border-surface-800 p-4 text-sm text-surface-400">
            <span className="mb-2 block font-mono text-xs text-brand-400">02</span>
            Yatırım tutarını ve saatini gönder. Sistem biletini hesaplar.
          </li>
          <li className="ticket-step rounded-xl border border-surface-800 p-4 text-sm text-surface-400">
            <span className="mb-2 block font-mono text-xs text-brand-400">03</span>
            Başvurun onaylandığında biletlerin çekilişe eklenir; ret nedenini bu sayfada
            görürsün.
          </li>
        </ol>
      </section>
    </div>
  );
}
